import {
    Typography,
    IconButton,
    Card,
    CardContent,
    CircularProgress,
    Box,
    Chip,
    Collapse,
    Divider,
    Tooltip
} from '@mui/material';
import { ExpandMore, ExpandLess, Edit } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { fetchGroupByUUID } from '../../../../services/groups';
import SemiCircularProgress from './SemiCircularProgress';
import { t } from 'i18next';

const getProgress = (start, end) => {
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    const today = dayjs();

    const total = endDate.diff(startDate, 'day');
    const current = today.diff(startDate, 'day');

    if (current <= 0) return 0;
    if (current >= total) return 100;

    return Math.round((current / total) * 100);
};

const GroupCard = ({ group, onView, onEdit, isFinished }) => {
    const progress = getProgress(group.start_date, group.end_date);
    const headerColor = isFinished ? '#388e3c' : '#1976d2';
    const [loading, setLoading] = useState(true);
    const [selectedDoctors, setSelectedDoctors] = useState([]);
    const [totalPatients, setTotalPatients] = useState([]);
    const [description, setDescription] = useState([]);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const fetchGroup = async () => {
            try {
                const data = await fetchGroupByUUID(group.uuid);
                const doctors = data.staff?.map((p) => p.name) || [];
                const patientsTotal = data.patients?.length || 0;
                setSelectedDoctors(doctors);
                setTotalPatients(patientsTotal);
                setDescription(data.description);
            } catch (error) {
                console.error('Erro ao procurar grupo:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchGroup();
    }, [group]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="300px"
            >
                <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
        );
    }

    return (
        <Card
            variant="outlined"
            sx={{
                p: 0,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    cursor: 'pointer'
                }
            }}
            onClick={onView}
        >
            {/* HEADER */}
            <Box
                sx={{
                    backgroundColor: headerColor,
                    color: 'white',
                    px: 2,
                    py: 1.2,
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Typography variant="h6" fontWeight="bold" color="inherit">
                    {group.name}
                </Typography>
                <Tooltip title="Editar Grupo" arrow>
                    <IconButton
                        size="small"
                        sx={{ color: 'white' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit();
                        }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* BODY 2-column */}
            <CardContent>
                <Box display="flex" gap={2} alignItems="center">
                    {/* LEFT COLUMN */}
                    <Box
                        flex={1}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <SemiCircularProgress
                            value={progress}
                            color={headerColor}
                            size={160}
                            stroke={8}
                        />
                    </Box>

                    {/* VERTICAL DIVIDER */}
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                            borderColor: headerColor,
                            borderWidth: 2,
                            opacity: 0.5
                        }}
                    />

                    {/* RIGHT COLUMN */}
                    <Box flex={2}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {description}
                        </Typography>

                        {/* Dates */}
                        <Box display="flex" flexDirection="column">
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            >
                                <span style={{ color: headerColor }}>
                                    <strong>{t('groups.total_patients')}:</strong>
                                </span>{' '}
                                {totalPatients}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            >
                                <strong>{t('groups.start_date')}:</strong>{' '}
                                {dayjs(group.start_date).format('DD/MM/YYYY')}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            >
                                <strong>{t('groups.end_date')}:</strong>{' '}
                                {dayjs(group.end_date).format('DD/MM/YYYY')}
                            </Typography>
                        </Box>

                        {/* Doctors */}
                        {selectedDoctors.length > 0 && (
                            <Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Typography variant="body2">
                                        <span style={{ color: headerColor }}>
                                            <strong>{t('groups.responsibles')}:</strong>
                                        </span>{' '}
                                        {selectedDoctors[0]}
                                        {selectedDoctors.length > 1 &&
                                            ` +${selectedDoctors.length - 1} outros`}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpanded(!expanded);
                                        }}
                                    >
                                        {expanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                </Box>
                                <Collapse in={expanded}>
                                    <Box mt={1}>
                                        {selectedDoctors.map((doc, i) => (
                                            <Chip
                                                key={i}
                                                label={doc}
                                                size="small"
                                                sx={{ mr: 0.5, mb: 0.5 }}
                                            />
                                        ))}
                                    </Box>
                                </Collapse>
                            </Box>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default GroupCard;
